import React, { forwardRef } from 'react';
import { Collapsible } from '@base-ui/react/collapsible';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

export interface CollapseProps {
  /** Expands the panel when `true` and collapses it when `false`. */
  in?: boolean;
  /**
   * The duration of the height transition in milliseconds, or `'auto'` for a duration derived
   * from the panel's height, on the curve of MUI's `timeout="auto"`: about 240ms for 100px and
   * 440ms for 1,000px.
   * @default 300
   */
  timeout?: 'auto' | number;
  /**
   * Removes the content from the DOM once the closing transition ends. Without it, the closed
   * content stays mounted (keeping its state), and the panel stays in the layout at height 0 with
   * `visibility: hidden`.
   */
  unmountOnExit?: boolean;
  children?: React.ReactNode;
  /** Applied to the panel, the element whose height animates. */
  className?: string;
}

// Base UI writes the measured content height into --collapsible-panel-height while a transition
// runs, and `auto` otherwise. A closed, kept-mounted panel carries Base UI's `hidden` attribute,
// but stays in the layout at height 0 with `visibility: hidden`: `flow-root` beats the browser's
// own `[hidden] { display: none }`. Leaving the layout would drop the panel out of a parent's flex
// or grid gap the moment a close ends, and add the gap back as an open starts. `flow-root` also
// keeps the content's margins inside the panel, so they do not collapse through it while open and
// jump when the transition's `overflow: hidden` ends. Timing and overflow live in the
// `collapse-panel` rules in index.css.
const panelLayoutClasses =
  'flow-root h-[var(--collapsible-panel-height)] data-[ending-style]:h-0 data-[starting-style]:h-0 [&[hidden]]:invisible [&[hidden]]:h-0';

const Collapse = forwardRef<HTMLDivElement, CollapseProps>(
  ({ in: inProp = false, timeout = 300, unmountOnExit = false, children, className, ...restProps }, ref) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Collapse: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the CollapseProps interface and to the component's signature.`,
        true
      );
    }

    // A numeric timeout sets the duration inline; 'auto' leaves it to the height-based rule.
    const durationStyle = timeout === 'auto' ? undefined : { '--collapse-duration': `${timeout}ms` };

    return (
      // The root only provides the open state to the panel. Rendering its children alone drops
      // its <div>, so an unmounted panel leaves nothing behind.
      <Collapsible.Root open={inProp} render={(rootProps) => <>{rootProps.children}</>}>
        <Collapsible.Panel
          data-fs-component="collapse"
          ref={ref}
          keepMounted={!unmountOnExit}
          className={cn('collapse-panel', timeout === 'auto' && 'collapse-panel-auto', panelLayoutClasses, className)}
          style={durationStyle}
        >
          {children}
        </Collapsible.Panel>
      </Collapsible.Root>
    );
  }
);

Collapse.displayName = 'Collapse';

export default Collapse;
